import { Box } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { getDateRangeByFilter, shouldFetchForFilter } from '../../helpers/dateRangeHelper';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';
import ChartFilter from '../../helpers/chartFilter';
import { generateDateRangeIntervals } from '../../helpers/dateRangeInterval';
import { fetchFacebookInsights, fetchInstagramViews, processInsightsData } from '../../helpers/metricHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler
);

export const ViewsChart = () => {
  const { tool_state, social_media_insight_state } = useContext(ToolContext)
  const { userId } = tool_state
  const platform = social_media_insight_state.connectedPlatform.data;

  const listView = async () => {
    if (platform == '' || social_media_insight_state.showProfilesModal.data) return;
    try {
      social_media_insight_state.viewsLoading.set(true);
      const dateRange = getDateRangeByFilter(
        social_media_insight_state.viewFilter.data,
        social_media_insight_state.viewCustomRange.data
      );
      let insightsData = [];
      if (platform == 'facebook') {
        const chartData = await fetchFacebookInsights({
          profileId: social_media_insight_state.selectedProfileId.data,
          accessToken: social_media_insight_state.accessToken.data,
          startDate: dateRange.start_date_unix,
          endDate: dateRange.end_date_unix,
        });
        insightsData = chartData || [];
      } else if (platform == 'instagram') {
        const reach = await fetchInstagramViews(social_media_insight_state.instagramAccountId.data, social_media_insight_state.instagramAccessToken.data, dateRange.start_date_unix, dateRange.end_date_unix);
        insightsData = reach || [];
      }

      let viewsByDate = null;
      if (platform == 'instagram') {
        viewsByDate = Object.entries(insightsData).map(([date, value]) => ({
          date,
          value
        }));
      } else {
        viewsByDate = processInsightsData({
          insightsData,
          startDate: dateRange.start_date,
          endDate: dateRange.end_date,
          targetMetric: 'page_media_view'
        });
      }

      const viewsData = generateDateRangeIntervals(
        dateRange.start_date,
        dateRange.end_date,
        viewsByDate,
        platform == 'instagram' ? 'views' : 'page_media_view'
      );
      social_media_insight_state.viewsData.set(viewsData);
    } catch (error) {
      console.error('Error fetching social media insights:', error);
      social_media_insight_state.viewsData.set([]);
    } finally {
      social_media_insight_state.viewsLoading.set(false);
    }
  };

  useEffect(() => {
    if (shouldFetchForFilter(social_media_insight_state.viewFilter.data, social_media_insight_state.viewCustomRange.data)) {
      listView();
    }
  }, [social_media_insight_state.viewFilter.data, social_media_insight_state.viewCustomRange.data, platform, social_media_insight_state.urlCode.data]);

  if (!userId?.data) {
    return null;
  }

  const viewsData = social_media_insight_state?.viewsData?.data || [];
  const labels = viewsData?.map(item => item.date);
  const barData = viewsData?.map(item => item.value);
  const yAxisConfig = calculateYAxisConfig(barData, {
    maxTicks: 7,
    padding: 10,
    roundToNearest: true
  });

  const data = {
    labels,
    datasets: [
      {
        fill: false,
        data: barData,
        borderColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return null;
          }
          const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
          gradient.addColorStop(0.00, '#FFFFFF');
          gradient.addColorStop(0.16, '#FF8B67');
          gradient.addColorStop(0.30, '#F6552D');
          gradient.addColorStop(0.71, '#F6552D');
          gradient.addColorStop(0.86, '#FF8B67');
          gradient.addColorStop(1.00, '#FFFFFF');
          return gradient;
        },
        borderWidth: 4,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgb(255, 107, 107)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          font: {
            size: 12
          }
        },
        border: {
          display: false
        }
      },
      y: {
        ...yAxisConfig,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        border: {
          display: false
        }
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#061D22',
        padding: 12,
        cornerRadius: 18,
        displayColors: false,
        titleFont: {
          size: 0
        },
        bodyFont: {
          size: 14,
          weight: '600'
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            const value = context.parsed.y;
            return `${value >= 1000 ? `${value / 1000}k` : value} views`;
          },
        },
        position: 'nearest',
        intersect: false,
        mode: 'index',
      },
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    interaction: {
      intersect: false,
      mode: 'index',
    }
  };

  const plugins = [{
    id: 'verticalHoverLine',
    beforeDraw: (chart) => {
      if (chart.tooltip?._active?.length) {
        const activePoint = chart.tooltip._active[0];
        const ctx = chart.ctx;
        const x = activePoint.element.x;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;
        const gradient = ctx.createLinearGradient(0, topY, 0, bottomY);
        gradient.addColorStop(0, 'rgba(252, 252, 253, 0)');
        gradient.addColorStop(1, 'rgba(247, 205, 46, 0.15)');
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 27;
        ctx.strokeStyle = gradient;
        ctx.stroke();
        ctx.restore();
      }
    }
  }];

  return (
    <Box className='social-media-insight-chart-box'>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box fontSize={18} fontWeight={700} mb={2}>
          Views
        </Box>
        <Box display="flex" gap={2}>
          <ChartFilter
            filterValue={social_media_insight_state.viewFilter.data}
            setFilterValue={social_media_insight_state.viewFilter.set}
            customRange={social_media_insight_state.viewCustomRange.data}
            setCustomRange={social_media_insight_state.viewCustomRange.set}
            // minDate={dayjs().subtract(2, 'year').toDate()}
            maxDate={dayjs().toDate()}
          />
        </Box>
      </Box>
      <Box sx={{ height: '300px', width: '100%' }}>
        {social_media_insight_state.viewsLoading.data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
            <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
          </ Box>
        ) :
          (viewsData.length > 0 && viewsData.find(m => m.value > 0)) ? (
            <Line
              data={data}
              options={options}
              plugins={plugins}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
              No data available
            </Box>
          )}
      </Box>
    </Box>
  );
};